// src/pages/Performance/EvaluateAppraisal/evaluateAppraisal.utils.js

/* ---------------------------------------
   API Paths
---------------------------------------- */
export const API = {
  showAppraisal: (id) => `/performance/appraisals/${id}`,
  submitStep: (appraisalId, stepId) =>
    `/performance/appraisals/${appraisalId}/steps/${stepId}`,
  stepVersions: (appraisalId, stepId) =>
    `/performance/appraisals/${appraisalId}/steps/${stepId}/versions`,
  listQuestions: (params = "") => `/performance/questions?per_page=500${params}`,
};

/* ---------------------------------------
   Rating constants
---------------------------------------- */
export const RATING_MIN = 0;
export const RATING_MAX = 5;
export const RATING_STEP = 0.1;

/* ---------------------------------------
   Error helpers
---------------------------------------- */
export const safeMsg = (err, fallback = "Something went wrong") => {
  const status = err?.response?.status;
  const serverMsg =
    err?.response?.data?.message ||
    err?.response?.data?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  if (!err?.response && String(err?.message || "").toLowerCase().includes("network")) {
    return "Network error. Please check your connection and try again.";
  }

  if (status === 401) return "Your session has expired. Please login again.";
  if (status === 403) return "You don’t have permission to access this appraisal/step.";
  if (status === 404) return "Not found. This appraisal/step may have been deleted.";
  if (status === 409)
    return "Conflict. This step might have been updated by someone else. Please refresh and try again.";
  if (status === 419) return "Security token expired. Please refresh the page and try again.";
  if (status === 422) return serverMsg || "Please correct the highlighted fields and try again.";

  return serverMsg || fallback;
};

export const extractLaravelErrors = (err) => {
  const errors =
    err?.response?.data?.errors ||
    err?.response?.data?.data?.errors ||
    err?.response?.data?.data?.data?.errors ||
    null;
  return errors && typeof errors === "object" ? errors : null;
};

/* ---------------------------------------
   Status labels/colors
---------------------------------------- */
export const STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending Review",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const STATUS_COLOR = {
  draft: "default",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  cancelled: "error",
};

export const STEP_STATUS_COLOR = {
  not_started: "default",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  submitted: "success",
  skipped: "default",
};

/* ---------------------------------------
   Date formatting
---------------------------------------- */
export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/* ---------------------------------------
   Data shape helpers
---------------------------------------- */
export const getStepInstances = (appraisal) => {
  if (!appraisal) return [];
  return appraisal.step_instances || appraisal.stepInstances || [];
};

export const getStepLabel = (step) =>
  step?.step_name || step?.label || step?.name || `Step #${step?.step_order ?? "—"}`;

export const normalizeQuestionBank = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const pick = (obj, key) => obj?.[key] ?? obj?.data?.[key] ?? obj?.data?.data?.[key] ?? null;

/* ---------------------------------------
   Self review detection
---------------------------------------- */
export const detectSelfReview = (appraisal, step) => {
  if (!appraisal || !step) return false;

  const employeeId = Number(appraisal?.employee?.id);
  const stepUserId = Number(step?.user_id ?? step?.user?.id);

  if (employeeId && stepUserId && employeeId === stepUserId) return true;

  const hay = String(
    step?.role_key ||
      step?.actor_type ||
      step?.step_name ||
      step?.label ||
      step?.name ||
      ""
  )
    .toLowerCase()
    .trim();

  return (
    hay.includes("self") ||
    hay.includes("employee") ||
    hay.includes("self_review") ||
    hay.includes("selfreview")
  );
};

/* ---------------------------------------
   Answers extraction + checks
---------------------------------------- */
export const extractAnswers = (latestSubmission) => {
  const candidates = [
    latestSubmission?.answers,
    latestSubmission?.responses,
    latestSubmission?.items,
    latestSubmission?.data?.answers,
    latestSubmission?.data?.responses,
    latestSubmission?.data?.items,
    latestSubmission?.data?.data?.answers,
    latestSubmission?.data?.data?.responses,
    latestSubmission?.data?.data?.items,
  ];
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
};

export const isMeaningfulAnswerLike = (
  { rating, comments, applicability },
  { selfReview = false } = {}
) => {
  const c = String(comments ?? "").trim();
  if (selfReview) return c.length > 0;

  const r = rating === null || rating === undefined ? "" : String(rating).trim();
  const a = String(applicability ?? "").trim();
  return c.length > 0 || r.length > 0 || (a.length > 0 && a !== "YES");
};

export const hasAnyMeaningfulAnswer = (rows, { selfReview = false } = {}) =>
  (rows || []).some((r) =>
    isMeaningfulAnswerLike(
      { rating: r.rating, comments: r.comments, applicability: r.applicability },
      { selfReview }
    )
  );

/* ---------------------------------------
   Build rows (bank + step responses)
---------------------------------------- */
export const buildQuestionsFromBank = (step, bankQuestions, { selfReview = false } = {}) => {
  const responses = step?.responses || [];
  const map = new Map();

  responses.forEach((r) => {
    const qid = Number(r.question_id ?? r.question?.id);
    if (!Number.isNaN(qid)) map.set(qid, r);
  });

  const rows = (bankQuestions || []).map((q) => {
    const qid = Number(q.id);
    const resp = map.get(qid);

    const rating = selfReview ? "" : resp?.rating == null ? "" : String(resp.rating);
    const comments = resp?.comments || "";
    const applicability = selfReview ? "YES" : resp?.applicability || q.applicability || "YES";

    const meaningful = resp
      ? isMeaningfulAnswerLike({ rating, comments, applicability }, { selfReview })
      : false;

    return {
      questionId: qid,
      questionTitle: q.title || q.name || `Question #${qid}`,
      questionDescription: q.description || "",
      rating,
      comments,
      applicability,
      _hasExistingAnswer: meaningful,
      _prefilledFromStep: null,
    };
  });

  // keep responses for removed questions
  responses.forEach((r) => {
    const qid = Number(r.question_id ?? r.question?.id);
    if (!qid) return;

    const existsInRows = rows.some((x) => Number(x.questionId) === qid);
    if (existsInRows) return;

    const rating = selfReview ? "" : r.rating == null ? "" : String(r.rating);
    const comments = r.comments || "";
    const applicability = selfReview ? "YES" : r.applicability || "YES";

    const meaningful = isMeaningfulAnswerLike({ rating, comments, applicability }, { selfReview });

    rows.push({
      questionId: qid,
      questionTitle: r.question?.title || `Question #${qid}`,
      questionDescription: "",
      rating,
      comments,
      applicability,
      _hasExistingAnswer: meaningful,
      _prefilledFromStep: null,
    });
  });

  return rows;
};

export const mergeSubmissionAnswersIntoRows = (rows, latestSubmission, { selfReview = false } = {}) => {
  const answers = extractAnswers(latestSubmission);
  if (!answers.length) return rows;

  const map = new Map();
  answers.forEach((a) => {
    const qid = Number(a.question_id ?? a.question?.id ?? a.questionId);
    if (!Number.isNaN(qid)) map.set(qid, a);
  });

  const merged = (rows || []).map((r) => {
    const a = map.get(Number(r.questionId));
    if (!a) return r;

    const next = {
      ...r,
      rating: selfReview ? "" : a.rating == null ? "" : String(a.rating),
      comments: a.comments ?? "",
      applicability: selfReview ? "YES" : a.applicability || r.applicability || "YES",
      _prefilledFromStep: r._prefilledFromStep ?? null,
    };

    next._hasExistingAnswer = isMeaningfulAnswerLike(
      { rating: next.rating, comments: next.comments, applicability: next.applicability },
      { selfReview }
    );

    return next;
  });

  // add answers not in bank
  answers.forEach((a) => {
    const qid = Number(a.question_id ?? a.question?.id ?? a.questionId);
    if (!qid) return;

    const exists = merged.some((x) => Number(x.questionId) === qid);
    if (exists) return;

    const row = {
      questionId: qid,
      questionTitle: a.question?.title || a.question_title || `Question #${qid}`,
      questionDescription: "",
      rating: selfReview ? "" : a.rating == null ? "" : String(a.rating),
      comments: a.comments ?? "",
      applicability: selfReview ? "YES" : a.applicability || "YES",
      _prefilledFromStep: null,
    };

    row._hasExistingAnswer = isMeaningfulAnswerLike(
      { rating: row.rating, comments: row.comments, applicability: row.applicability },
      { selfReview }
    );

    merged.push(row);
  });

  return merged;
};

export const prefillFromSourceRows = (
  targetRows,
  sourceRows,
  sourceStepOrder,
  { selfReview = false } = {}
) => {
  const srcMap = new Map();
  (sourceRows || []).forEach((r) => {
    srcMap.set(Number(r.questionId), {
      rating: r.rating ?? "",
      comments: r.comments ?? "",
      applicability: r.applicability ?? "YES",
    });
  });

  return (targetRows || []).map((r) => {
    const src = srcMap.get(Number(r.questionId));
    if (!src) return r;

    const next = { ...r };
    let touched = false;

    if (selfReview) {
      if (!String(next.comments ?? "").trim() && String(src.comments ?? "").trim()) {
        next.comments = src.comments ?? "";
        touched = true;
      }
    } else {
      if (!String(next.rating ?? "").trim() && String(src.rating ?? "").trim()) {
        next.rating = src.rating;
        touched = true;
      }

      if (
        (!String(next.applicability ?? "").trim() || String(next.applicability) === "YES") &&
        String(src.applicability ?? "").trim() &&
        String(src.applicability) !== "YES"
      ) {
        next.applicability = src.applicability;
        touched = true;
      }

      if (!String(next.comments ?? "").trim() && String(src.comments ?? "").trim()) {
        next.comments = src.comments ?? "";
        touched = true;
      }
    }

    if (touched) {
      next._prefilledFromStep = sourceStepOrder;
      next._hasExistingAnswer = isMeaningfulAnswerLike(
        { rating: next.rating, comments: next.comments, applicability: next.applicability },
        { selfReview }
      );
    }

    return next;
  });
};

/* ---------------------------------------
   Baseline + diff
---------------------------------------- */
export const buildBaselineMap = (rows) => {
  const map = {};
  (rows || []).forEach((r) => {
    const qid = Number(r.questionId);
    if (!qid) return;
    map[qid] = {
      rating: r.rating ?? "",
      comments: r.comments ?? "",
      applicability: r.applicability ?? "YES",
      has: !!r._hasExistingAnswer,
    };
  });
  return map;
};

const normStr = (v) => (v == null ? "" : String(v));

export const isRowChanged = (current, baseline, { selfReview = false } = {}) => {
  if (!baseline?.has) return false;

  const cComments = normStr(current?.comments).trim();
  const bComments = normStr(baseline?.comments).trim();

  if (selfReview) return cComments !== bComments;

  const cRating = normStr(current?.rating).trim();
  const bRating = normStr(baseline?.rating).trim();

  const cApp = normStr(current?.applicability).trim();
  const bApp = normStr(baseline?.applicability).trim();

  return cRating !== bRating || cComments !== bComments || cApp !== bApp;
};

/* ---------------------------------------
   Rating helpers
---------------------------------------- */
export const roundToStep = (n, step = RATING_STEP) => {
  const s = Number(step) || 1;
  return Math.round(n / s) * s;
};

const formatRatingStr = (n) => {
  if (!Number.isFinite(n)) return "";
  const fixed = Math.round(n * 10) / 10;
  return Number.isInteger(fixed) ? String(fixed.toFixed(0)) : String(fixed.toFixed(1));
};

export const clampRatingStr = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const n = Number(s);
  if (!Number.isFinite(n)) return "";
  const clamped = Math.min(RATING_MAX, Math.max(RATING_MIN, n));
  const stepped = roundToStep(clamped, RATING_STEP);
  return formatRatingStr(stepped);
};

export const normalizeRatingNum = (v) => {
  const s = clampRatingStr(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export const computeOverall = (rows) => {
  const eligible = (rows || []).filter((q) => String(q.applicability || "YES") !== "NO");
  const nums = eligible
    .map((q) => normalizeRatingNum(String(q.rating ?? "").trim()))
    .filter((n) => n != null);

  if (!nums.length) return { value: "", count: 0 };

  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  const stepped = roundToStep(Math.min(RATING_MAX, Math.max(RATING_MIN, avg)), RATING_STEP);

  return { value: String(stepped.toFixed(1)), count: nums.length };
};

/* ---------------------------------------
   Misc
---------------------------------------- */
export const toBool = (v) => {
  if (v === true || v === 1 || v === "1") return true;
  const s = String(v ?? "").trim().toLowerCase();
  return s === "yes" || s === "true" || s === "y";
};

export const initialsFromName = (name) => {
  const s = String(name || "").trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (a + b).toUpperCase();
};

/* ---------------------------------------
   Viewer helpers (localStorage)
---------------------------------------- */
const safeJsonParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getStoredAuthUser = () => {
  if (typeof window === "undefined") return null;

  const keys = ["auth_user", "authUser", "user", "current_user", "currentUser", "me", "profile"];
  for (const k of keys) {
    const raw = window.localStorage?.getItem?.(k);
    if (!raw) continue;

    const parsed = safeJsonParse(raw);
    if (!parsed || typeof parsed !== "object") continue;

    return parsed?.data?.user || parsed?.user || parsed;
  }

  return null;
};

/* ✅ REQUIRED by EvaluateAppraisalPage.jsx */
export const resolveCurrentUser = () => getStoredAuthUser();

/* ✅ REQUIRED by EvaluateAppraisalPage.jsx */
export const isViewerSameEmployee = (viewer, appraisal) => {
  const v = viewer || resolveCurrentUser();

  const vCode =
    v?.employee_code ?? v?.employeeCode ?? v?.employee_id ?? v?.employeeId ?? v?.id ?? null;

  const aCode =
    appraisal?.employee?.employee_code ??
    appraisal?.employee?.employeeCode ??
    appraisal?.employee_code ??
    appraisal?.employeeCode ??
    appraisal?.employee_id ??
    appraisal?.employeeId ??
    appraisal?.employee?.id ??
    null;

  if (vCode == null || aCode == null) return false;
  return String(vCode) === String(aCode);
};

export const roleKeyOf = (u) => {
  const raw = String(u?.actual_role || u?.role || u?.role_key || u?.type || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("hr")) return "hr_admin";
  if (raw.includes("ceo") || raw.includes("gm")) return "ceo";
  if (raw.includes("finance")) return "finance";
  if (raw.includes("manager") || raw.includes("project_manager") || raw.includes("supervisor"))
    return "manager";
  return raw;
};
